"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { NavLink } from "react-router-dom"
import { AmyPillButton } from "@/components/ui/amy-pill-button"
import { useI18n } from "@/i18n"
import { navItems } from "./navItems"

interface BottomNavProps {
  onOpenAmy: () => void
}

/**
 * Mobile (<768px) bottom navigation: Today · Plan · Shop · Insights ·
 * More, plus a persistent Amy button positioned above the bar so it never
 * covers the primary nav controls, per ui-ux-spec.md.
 */
export function BottomNav({ onOpenAmy }: BottomNavProps) {
  const { t } = useI18n()

  return (
    <>
      <nav
        aria-label={t("nav.primaryNavigation")}
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-paper md:hidden"
      >
        {navItems.map(({ to, labelKey, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              // `min-w-0` is the fix, not the font size (2026-09-10). A flex item
              // defaults to `min-width: auto`, so `flex-1` could not shrink these
              // below their LABEL width: German "Einstellungen" / "Einkaufen" /
              // "Einblicke" pushed the bar wider than a 390px viewport and the
              // labels collided. With `min-w-0` the seven tabs share the width
              // evenly and a long label wraps inside its own tab instead. No
              // destination is dropped and no approved term is shortened.
              `group flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 font-medium transition-colors ${
                isActive ? "text-green" : "text-ink-2"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {/* 2026-08 UI pass: the active tab's icon sits in a soft lime
                    pill — a much clearer "you are here" than color alone. */}
                <span
                  className={`flex items-center justify-center rounded-bb-full px-2.5 py-1 transition-colors sm:px-3.5 ${
                    isActive ? "bg-lime/60" : "bg-transparent"
                  }`}
                >
                  <Icon className="size-5 shrink-0" aria-hidden="true" />
                </span>
                {/* the label owns the full tab width and may take a second line;
                    German hyphenates rather than clipping or overflowing */}
                <span className="hyphens-auto w-full px-0.5 text-center text-[10.5px] leading-tight break-words">
                  {t(labelKey)}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <AmyPillButton
        label={t("amy.askAmy")}
        onClick={onOpenAmy}
        className="fixed right-4 bottom-20 z-40 md:hidden"
      />
    </>
  )
}
